const SEARCH_KEYS = new Set([
  "q",
  "location",
  "location_country",
  "date_from",
  "date_to",
  "passengers",
  "radius",
  "country",
  "country_code",
  "make",
  "car_type",
  "features",
  "price_min",
  "price_max",
  "page",
  "sort",
  "era",
  "colours",
  "rental_type",
]);

const RENTAL_TYPES = new Set(["driving", "chauffeured", "event"]);
const KNOWN_MODELS = [
  { pattern: /\bMX[\s-]?5\b/i, model: "MX5", make: "Mazda" },
  { pattern: /\bE[\s-]?Type\b/i, model: "E-Type", make: "Jaguar" },
  { pattern: /\bMustang\b/i, model: "Mustang", make: "Ford" },
  { pattern: /\b911\b/i, model: "911", make: "Porsche" },
  { pattern: /\bBeetle\b/i, model: "Beetle", make: "Volkswagen" },
  { pattern: /\bMGB\b/i, model: "MGB", make: "MG" },
  { pattern: /\bDB5\b/i, model: "DB5", make: "Aston Martin" },
  { pattern: /\bDefender\b/i, model: "Defender", make: "Land Rover" },
  { pattern: /\bSilver Shadow\b/i, model: "Silver Shadow", make: "Rolls-Royce" },
  { pattern: /\bPagoda\b/i, model: "Pagoda", make: "Mercedes-Benz" },
];

function toStateRecord(input) {
  if (input instanceof URLSearchParams) {
    const result = {};
    for (const [key, value] of input.entries()) {
      const previous = result[key];
      result[key] = previous === undefined
        ? value
        : Array.isArray(previous)
          ? [...previous, value]
          : [previous, value];
    }
    return result;
  }
  return input && typeof input === "object" ? { ...input } : {};
}

function nonEmptyString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function normalizeCountryCode(value) {
  const normalized = String(value || "").trim().toUpperCase();
  const code = normalized === "UK" ? "GB" : normalized;
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function explicitLandingContext(page) {
  const cluster = page.cluster && typeof page.cluster === "object" ? page.cluster : {};
  const clusterGeo = page.cluster_geo || page.clusterGeo || cluster.geo || {};
  const filterParams = page.filter_params || page.filterParams || cluster.filter_params || {};
  return {
    ...(clusterGeo && typeof clusterGeo === "object" ? clusterGeo : {}),
    ...(cluster && typeof cluster === "object" ? cluster : {}),
    ...(page.geo && typeof page.geo === "object" ? page.geo : {}),
    ...(page.cluster_inputs && typeof page.cluster_inputs === "object" ? page.cluster_inputs : {}),
    ...(filterParams && typeof filterParams === "object" ? filterParams : {}),
    ...(page.searchContext && typeof page.searchContext === "object" ? page.searchContext : {}),
    ...Object.fromEntries(
      ["location", "location_country", "city", "state", "region", "country", "country_code", "countryCode", "make", "model", "q", "rental_type", "car_type", "era"]
        .map((key) => [key, page[key]])
        .filter(([, value]) => value != null && value !== ""),
    ),
  };
}

function inferEnglishCityTitle(page) {
  const language = String(page.language || page.locale || "").toLowerCase().split(/[-_]/)[0];
  if (language && language !== "en") return undefined;
  if (String(page.page_type || "").toLowerCase() !== "city") return undefined;
  const titles = [page.hero_heading, page.name, page.page_title].map(nonEmptyString).filter(Boolean);
  const serviceTail = /(?:classic\s+|vintage\s+|wedding\s+|chauffeured\s+|self[- ]drive\s+|sports\s+|luxury\s+)*(?:wedding\s+)?(?:classic\s+)?(?:cars?|MX[\s-]?5|E[\s-]?Type|Mustang|911|Beetle|MGB|DB5|Defender|Silver\s+Shadow|Pagoda)\s+(?:car\s+)?(?:hire|rental|rent)\s+(?:in\s+)?(.+)$/i;
  for (const title of titles) {
    const cleanTitle = title.split(/\s+[|—–]\s+/)[0].trim();
    const match = cleanTitle.match(serviceTail);
    const inferred = match?.[1]?.trim().replace(/[.,;:!?]+$/, "");
    if (inferred && inferred.length <= 60 && !/^(?:cars?|hire|rental|rent)$/i.test(inferred)) {
      return inferred;
    }
  }
  return undefined;
}

/**
 * Resolve intent from explicit landing filters/cluster geo first, then from
 * conservative English title conventions for declared city rows. URL slugs are
 * deliberately never inspected. The optional country is an ISO-2 fallback
 * (with the common market alias `UK` normalized to the search route's `GB`).
 */
export function buildLandingSearchContextForPage(page = {}, country) {
  const context = explicitLandingContext(page);
  if (!context.location && !context.city && !context.state && !context.region) {
    const inferredLocation = inferEnglishCityTitle(page);
    if (inferredLocation) context.location = inferredLocation;
  }

  for (const title of [page.hero_heading, page.name, page.page_title].map(nonEmptyString).filter(Boolean)) {
    const knownModel = KNOWN_MODELS.find(({ pattern }) => pattern.test(title));
    if (!knownModel) continue;
    if (!context.model && !context.q) context.q = knownModel.model;
    if (!context.make) context.make = knownModel.make;
    break;
  }

  return buildLandingSearchState(context, country);
}

/**
 * Whitelist search-route state accepted by the public /search page. Uses its
 * `country` anchor when a search has no location, and the country_code facet
 * when a concrete location is also present. Converts landing-only city/model
 * fields into supported location/query fields instead of leaking ignored params.
 */
export function buildLandingSearchState(context = {}, fallbackCountryCode) {
  const state = toStateRecord(context);

  if (!state.location) {
    const city = typeof state.city === "string" ? state.city.trim() : "";
    const region = typeof state.state === "string"
      ? state.state.trim()
      : typeof state.region === "string"
        ? state.region.trim()
        : "";
    state.location = city && region ? `${city}, ${region}` : city || region || undefined;
  }

  if (!state.q && typeof state.model === "string" && state.model.trim()) {
    state.q = state.model.trim();
  }
  if (typeof state.country === "string" && state.country !== "all") {
    const normalized = normalizeCountryCode(state.country);
    if (normalized) state.country = normalized;
    else delete state.country;
  }
  if (typeof state.country_code === "string") {
    const normalized = normalizeCountryCode(state.country_code);
    if (normalized) state.country_code = normalized;
    else delete state.country_code;
  }
  const countryCode = typeof state.country_code === "string"
    ? state.country_code
    : typeof state.country === "string" && state.country !== "all"
      ? state.country
      : "";
  if (!state.country && fallbackCountryCode) {
    const fallback = normalizeCountryCode(fallbackCountryCode);
    if (fallback) {
      if (state.location) state.country_code = fallback;
      else state.country = fallback;
    }
  }
  if (countryCode && /^[A-Z]{2}$/.test(countryCode)) {
    if (state.location) {
      state.country_code = countryCode;
      if (state.country !== "all") delete state.country;
    } else if (!state.country || state.country !== "all") {
      state.country = countryCode;
      delete state.country_code;
    }
  }
  if (typeof state.country_code === "string") {
    state.country_code = state.country_code.trim().toUpperCase();
  }
  if (typeof state.country === "string" && state.country !== "all") {
    state.country = state.country.trim().toUpperCase();
  }
  if (state.rental_type && !RENTAL_TYPES.has(String(state.rental_type))) {
    delete state.rental_type;
  }

  const result = {};
  for (const [key, value] of Object.entries(state)) {
    if (!SEARCH_KEYS.has(key) || value == null || value === "") continue;
    if (Array.isArray(value)) {
      const values = value
        .map((item) => String(item).trim())
        .filter(Boolean);
      if (values.length) result[key] = values;
    } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      result[key] = value;
    }
  }
  return result;
}

export function buildLandingSearchQuery(context = {}, fallbackCountryCode) {
  const params = new URLSearchParams();
  const state = buildLandingSearchState(context, fallbackCountryCode);
  for (const [key, value] of Object.entries(state)) {
    if (Array.isArray(value)) value.forEach((item) => params.append(key, String(item)));
    else params.set(key, String(value));
  }
  return params.toString();
}

export function buildLandingSearchHref(searchPath = "/search", context = {}, fallbackCountryCode) {
  const query = buildLandingSearchQuery(context, fallbackCountryCode);
  const [pathAndQuery, hash = ""] = String(searchPath).split("#", 2);
  const separator = pathAndQuery.includes("?") ? "&" : "?";
  return `${pathAndQuery}${query ? `${separator}${query}` : ""}${hash ? `#${hash}` : ""}`;
}